import type { CashAccountChoice } from '@core/api/cash';
import { accountsFor, defaultPaidFrom } from '@core/cash/paidFrom';
import type { PaymentMethod } from '@core/generated/enums';

/**
 * Which account a refund comes out of (split payments step 6, owner 2026-11-01) — the accounts
 * that can hand back this way (a till or petty cash for cash, a bank for the rest), the default
 * chosen until someone picks. Shown only when there is a choice; null sends nothing and the server
 * takes the method's default, as before. One account, not boxes: a settlement or a cancellation
 * writes one refund.
 */
export function AccountChoice({
  label = 'Out of which account?',
  method,
  accounts,
  value,
  onChange,
  disabled = false,
}: {
  label?: string;
  method: PaymentMethod;
  accounts: readonly CashAccountChoice[] | undefined;
  /** The account picked, or null for the method's default. */
  value: number | null;
  onChange: (accountId: number) => void;
  disabled?: boolean;
}) {
  const choices = accountsFor(method, accounts);
  const shown = value ?? defaultPaidFrom(method, accounts);

  if (choices.length < 2) {
    return null;
  }

  return (
    <div>
      <p className="mb-1.5 text-[12px] font-medium text-gray-900">{label}</p>
      <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
        {choices.map((account) => (
          <button
            key={account.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(account.id)}
            aria-pressed={account.id === shown}
            className={`cursor-pointer rounded border px-2.5 py-1 text-[12px] disabled:cursor-not-allowed disabled:opacity-40 ${
              account.id === shown
                ? 'bg-dark border-dark text-white'
                : 'border-border-color hover:bg-light bg-white text-gray-900'
            }`}
          >
            {account.name}
          </button>
        ))}
      </div>
    </div>
  );
}
